import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import type { CustomFormField, CustomFormAnswerItem } from '@/services/communityService';
import LevelToggle from '@/components/community/badminton/LevelToggle';

export interface EditableLevelField {
  value: string;
  options: readonly string[];
  saving?: boolean;
  onSave: (level: string) => Promise<void> | void;
}

interface CustomFormAnswersViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  participantName: string;
  answers?: CustomFormAnswerItem[];
  customFormQuestions?: CustomFormField[];
  editableLevel?: EditableLevelField;
}

const isLevelQuestion = (field?: CustomFormField) =>
  field?.template_type === 'level' || field?.template === 'level';

export const CustomFormAnswersViewModal: React.FC<CustomFormAnswersViewModalProps> = ({
  isOpen,
  onClose,
  participantName,
  answers = [],
  customFormQuestions = [],
  editableLevel,
}) => {
  const [draftLevel, setDraftLevel] = useState('');

  useEffect(() => {
    if (isOpen) setDraftLevel(editableLevel?.value || '');
  }, [isOpen, editableLevel?.value]);

  if (!isOpen) return null;

  const hasAnswers = answers.length > 0;
  const levelDirty = Boolean(editableLevel && draftLevel && draftLevel !== editableLevel.value);

  const levelEditor = editableLevel ? (
    <div className="space-y-2">
      <span className="text-sm text-gray-500 block">Level</span>
      <LevelToggle
        value={draftLevel}
        options={editableLevel.options}
        disabled={editableLevel.saving}
        onChange={setDraftLevel}
      />
    </div>
  ) : null;

  const rows = answers.filter((ans) => {
    const matched = customFormQuestions.find((q) => q.id === (ans.field_id || ans.question_id));
    return !(ans.template_type === 'level' || isLevelQuestion(matched));
  });
  const showLevelEditor = Boolean(editableLevel);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Jawaban Formulir"
      subtitle={`Data pendaftaran dari ${participantName}`}
      size="md"
      position="bottom"
    >
      <div className="space-y-4 pt-1 pb-2">
        {!hasAnswers && !showLevelEditor ? (
          <div className="text-center py-8 px-4">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400">
              <Icon icon="ph:clipboard-text-bold" className="w-6 h-6" />
            </div>
            <h4 className="text-sm font-bold text-[#0E1726] mb-1">Belum Ada Jawaban Formulir</h4>
            <p className="text-xs text-gray-500 max-w-xs mx-auto">
              Peserta ini belum mengisi pertanyaan tambahan atau mendaftar sebelum formulir diaktifkan.
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {showLevelEditor && (
              <div className="p-3.5 rounded-2xl bg-white border border-gray-200">{levelEditor}</div>
            )}
            {rows.map((ans, idx) => {
              const qId = ans.field_id || ans.question_id;
              const matchedQ = customFormQuestions.find((q) => q.id === qId);
              const questionTitle = ans.title || matchedQ?.title || `Pertanyaan #${idx + 1}`;
              const answerValue =
                ans.value !== undefined && ans.value !== null && String(ans.value).trim() !== ''
                  ? String(ans.value)
                  : '-';
              return (
                <div key={qId || idx} className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 space-y-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">
                    {questionTitle}
                  </span>
                  <p className="text-sm font-bold text-[#0E1726] break-words">{answerValue}</p>
                </div>
              );
            })}
            {rows.length === 0 && !showLevelEditor && (
              <p className="text-xs text-gray-500 text-center py-4">Belum ada jawaban formulir.</p>
            )}
          </div>
        )}

        <div className="pt-2 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 rounded-2xl py-2.5 font-bold border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Tutup
          </Button>
          {levelDirty && editableLevel && (
            <Button
              type="button"
              onClick={() => void editableLevel.onSave(draftLevel)}
              disabled={editableLevel.saving}
              className="flex-1 rounded-2xl py-2.5 font-bold bg-[#FC480D] hover:bg-[#D83B08] text-white"
            >
              {editableLevel.saving ? 'Menyimpan...' : 'Simpan Level'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CustomFormAnswersViewModal;
